import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SubmitError, SubmitErrorType, SubmitType, SubmitResult } from '@typesApp/SubmitError'
import { AppState } from '@redux/store'
import { workflowSlice } from './workflowSlice'

type SubmitState = {
  submitType: SubmitType
  submitError: SubmitError
  submitResult: SubmitResult
  submitting: boolean
}

const initialState: SubmitState = {
  submitType: SubmitType.DEFAULT,
  submitError: { type: SubmitErrorType.NONE },
  submitResult: SubmitResult.NONE,
  submitting: false,
}

export const claimSubmitSlice = createSlice({
  name: 'claimSubmit',
  initialState: initialState,
  reducers: {
    submitError(state, action: PayloadAction<SubmitError | SubmitErrorType>) {
      state.submitError = isSubmitError(action.payload) ? action.payload : { type: action.payload }
      state.submitResult = SubmitResult.ERROR
    },
    resetSubmitError(state) {
      state.submitError = { type: SubmitErrorType.NONE }
      state.submitResult = SubmitResult.NONE
    },
    setSubmitting(state, action: PayloadAction<boolean | { submitting: boolean; submitType: SubmitType }>) {
      state.submitting = typeof action.payload === 'boolean' ? action.payload : action.payload.submitting
      if (typeof action.payload !== 'boolean') {
        state.submitType = action.payload.submitType
      }
    },
    setSubmitResult(state, action: PayloadAction<SubmitResult>) {
      state.submitResult = action.payload
    },
    resetSubmitStatus(state) {
      return {
        ...initialState,

        // retain current submit state to allow feedback (modal) to be shown w/o the app drawer being visible
        submitError: state.submitError,
        submitResult: state.submitResult,
      }
    },
    resetClaimSubmitInitialState(state) {
      return initialState
    },
  },

  extraReducers: builder => {
    /**
     * Reset submit status on show drawer
     */
    builder.addCase(workflowSlice.actions.showApp, state => {
      state.submitError = { type: SubmitErrorType.NONE }
      state.submitResult = SubmitResult.NONE
    })
  },
})

const isSubmitError = (payload: SubmitError | SubmitErrorType): payload is SubmitError => {
  return (payload as SubmitError).type ? true : false
}

export const {
  submitError,
  setSubmitting,
  setSubmitResult,
  resetSubmitError,
  resetSubmitStatus,
  resetClaimSubmitInitialState,
} = claimSubmitSlice.actions

export const submitTypeSelector = (state: AppState): SubmitType => state.claimSubmit.submitType
export const submitResultSelector = (state: AppState): SubmitResult => state.claimSubmit.submitResult
export const submitErrorSelector = (state: AppState): SubmitError => state.claimSubmit.submitError
export const claimSubmittingSelector = (state: AppState): boolean => state.claimSubmit.submitting
